// Per-segment corridor NDVI, served as a WMS layer by GeoServer (issue #146,
// phase 3 of #136).
//
// WHY WMS AND NOT GeoJSON. The segment geometry and its values live in PostGIS,
// which the browser cannot reach and the Node backend does not proxy. GeoServer
// already publishes them, already applies the SLD that keeps a clouded segment
// from being drawn as a low reading, and already has the styling committed to
// this repository. Fetching the geometry and re-implementing that ramp in
// JavaScript would put the honesty rules in two places, and the second one would
// drift.
//
// WHY L.TileLayer.WMS AND NOT OpenLayers. `leaflet@^1.9.4` consumes WMS
// natively, and react-leaflet's <WMSTileLayer> is a thin wrapper over exactly
// `L.tileLayer.wms`. Migrating the map to OpenLayers for this is weeks of work
// on App.js — the repository's highest-conflict file — for no functional gain.
// #136 records the reasoning and #146 makes "no OpenLayers dependency added" an
// acceptance criterion.
//
// TWO REQUESTS, TWO PURPOSES, and they are not redundant:
//
//   * WMS GetMap draws the segments. Tiles, rendered server-side, styled by
//     infrastructure/geoserver/styles/samst_segment_ndvi_latest.sld.
//   * WFS GetFeature reads the same segments' NUMBERS, so the summary box can
//     say what the picture means in the operator's own language. A tile cannot
//     carry "clouded" or "measured 23 days ago" in Montenegrin; GeoServer's own
//     legend is English-only and is not translated by this project.
//
// TIER GATE. Both requests are made only for superusers, matching every /api/
// consumer in App.js. The OGC endpoints are unauthenticated by necessity — a
// Leaflet tile request is a plain browser GET and cannot carry an Authorization
// header, which is why nginx leaves /ows, /wms and /wfs open — so the gate here
// is the only thing that stops a signed-out visitor pulling real corridor
// measurements onto a map that is otherwise showing generated data.

import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { WMSTileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import { Box, Typography } from "@mui/material";
import HelpTip from "./HelpTip";

// The generic OGC dispatcher. nginx's allow-list re-opens exactly
// /geoserver/{ows,wms,wfs,gwc/service/} and 404s everything else, so the layers
// are addressed by their qualified name through /ows rather than through a
// workspace-prefixed path like /geoserver/samst/wms, which that allow-list does
// not match. A layer needing a path outside the set is a configuration error,
// not a reason to widen it (#138).
const OWS_PATH = "/geoserver/ows";
const WFS_PATH = "/geoserver/wfs";

export const SEGMENT_LAYER = "samst:segment_ndvi_latest";

// NDVI changes at most once per Sentinel-2 revisit, about every 5 days, and the
// measuring job runs daily. Six hours is the same cadence App.js already uses
// for the corridor-level satellite reading.
const SEGMENT_POLL_MS = 6 * 60 * 60 * 1000;

// Below this share of the footprint, the mean describes a fraction of the
// segment and the summary says so. Same threshold as App.js's
// NDVI_PARTIAL_VIEW_BELOW, for the same reason: a mean over 40% of a segment
// and a mean over all of it are different claims.
const PARTIAL_VIEW_BELOW = 0.75;

// More than three Sentinel-2 revisit cycles without a usable acquisition. The
// same 15 days the SLD draws a dashed outline at — the two must agree, or the
// map and the text beside it disagree about the same segment.
const CARRY_FORWARD_DAYS = 15;

// The ramp, in lockstep with samst_segment_ndvi_latest.sld. If one changes the
// other must: a swatch that disagrees with the polygon it labels is worse than
// no swatch.
const RAMP = [
  { below: 0.20, color: "#ffffcc" },
  { below: 0.35, color: "#c2e699" },
  { below: 0.50, color: "#78c679" },
  { below: 0.65, color: "#31a354" },
  { below: Infinity, color: "#006837" },
];

// No-data swatches are HATCHED, never a flat colour from the ramp — the same
// rule the SLD enforces, mirrored here so the legend cannot quietly present an
// absence as a value.
const HATCH = {
  cloud: "repeating-linear-gradient(45deg,#f1f5f9 0 3px,#64748b 3px 4px)",
  outside_footprint: "repeating-linear-gradient(45deg,#ffffff 0 3px,#94a3b8 3px 4px)",
  unavailable: "repeating-linear-gradient(45deg,#fef3c7 0 3px,#b45309 3px 4px)",
  none: "repeating-linear-gradient(-45deg,#e5e7eb 0 3px,#9ca3af 3px 4px)",
};

const rampColor = (v) => RAMP.find((r) => v < r.below).color;

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

// The per-segment numbers behind the tiles.
//
// `propertyName` deliberately omits both geometry columns. The shapes are
// already on screen as tiles; asking for them again would multiply the payload
// for nothing. What comes back is ~1.2 kB for the pilot's two segments.
export function useSegmentNdvi({ baseUrl, enabled, pollMs = SEGMENT_POLL_MS }) {
  const [state, setState] = useState(null);

  const fields = useMemo(() => [
    "segment_id", "seq", "ndvi_mean", "valid_fraction",
    "last_valid_at", "last_valid_age_days",
    "last_attempt_at", "last_attempt_status", "last_attempt_reason",
    "ndvi_vs_baseline", "baseline_n_obs", "never_measured",
  ].join(","), []);

  const load = useCallback(async (signal) => {
    const url = `${baseUrl}${WFS_PATH}?service=WFS&version=2.0.0&request=GetFeature` +
      `&typeNames=${encodeURIComponent(SEGMENT_LAYER)}&outputFormat=application/json` +
      `&sortBy=seq&propertyName=${fields}`;
    try {
      const res = await fetch(url, { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const segments = (body.features ?? []).map((f) => f.properties ?? {});
      // Zero segments is NOT an empty success. It means the corridor model has
      // not been generated, or the read role cannot see the view — both of which
      // must read as "we cannot tell you", never as "nothing to report".
      setState(segments.length
        ? { status: "ok", segments }
        : { status: "empty", segments: [] });
    } catch (e) {
      if (e.name === "AbortError") return;
      setState({ status: "unavailable", segments: [], error: e.message });
    }
  }, [baseUrl, fields]);

  useEffect(() => {
    if (!enabled) { setState(null); return; }
    const ctl = new AbortController();
    load(ctl.signal);
    const id = setInterval(() => load(ctl.signal), pollMs);
    return () => { ctl.abort(); clearInterval(id); };
  }, [enabled, pollMs, load]);

  return state;
}

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

const fmtNdvi = (v) => (typeof v === "number" ? v.toFixed(3) : null);

const fmtDelta = (v) =>
  typeof v === "number" ? `${v >= 0 ? "+" : "−"}${Math.abs(v).toFixed(3)}` : null;

function fmtAge(days, t) {
  if (typeof days !== "number") return null;
  if (days <= 0) return t("segmentAgeToday");
  return t("segmentAgeDays", { n: days });
}

// ---------------------------------------------------------------------------
// The summary box
//
// A Leaflet control rather than an absolutely positioned box in App.js: it
// belongs to the map, it must move with the map's corners, and keeping it here
// means the toggle costs App.js a handful of lines rather than a panel.
// ---------------------------------------------------------------------------

function MapControl({ position, children }) {
  const map = useMap();
  const [container, setContainer] = useState(null);

  useEffect(() => {
    const control = L.control({ position });
    control.onAdd = () => {
      const div = L.DomUtil.create("div");
      // Without both of these, a click on the summary reaches the map's own
      // click handler, which reads a map click as "add a sensor here" and opens
      // the add-pin dialog — the same trap the power-line and fire layers each
      // have to step around.
      L.DomEvent.disableClickPropagation(div);
      L.DomEvent.disableScrollPropagation(div);
      setContainer(div);
      return div;
    };
    control.addTo(map);
    return () => { control.remove(); setContainer(null); };
  }, [map, position]);

  return container ? createPortal(children, container) : null;
}

function Swatch({ background }) {
  return (
    <Box component="span" sx={{
      display: "inline-block", width: 12, height: 12, flexShrink: 0,
      borderRadius: "2px", border: "1px solid #6b7280", background,
    }} />
  );
}

function SegmentRow({ segment, t, nodeLabels = {} }) {
  const {
    segment_id, seq, ndvi_mean, valid_fraction, last_valid_age_days,
    last_attempt_status, ndvi_vs_baseline, baseline_n_obs,
  } = segment;

  // Name the span by its endpoints, never by its position in the list. The
  // ordinal is actively misleading here: seq 1 is node-002 → node-003 and seq 2
  // is node-003 → node-001, so "Segment 1" does not touch node 1 and "Segment
  // 2" is not the second node. A reader has no way to discover that from the
  // panel, and the obvious guess is wrong for both.
  //
  // segment_id is the pair that generated the row, so the endpoints are already
  // here — only the human labels have to come from the node list. Falling back
  // to the raw ids and then to the ordinal keeps something on screen if the
  // nodes have not loaded, in decreasing order of how much it tells you.
  const endpoints = typeof segment_id === "string" ? segment_id.split("__") : [];
  const spanLabel = endpoints.length === 2
    ? `${nodeLabels[endpoints[0]] ?? endpoints[0]} → ${nodeLabels[endpoints[1]] ?? endpoints[1]}`
    : t("segmentLabel", { n: seq });

  const measured = typeof ndvi_mean === "number";
  // A segment with no value is described by WHY, never by a number. The four
  // causes are the four states samst.segment_ndvi's CHECK constraint allows.
  const reasonKey = measured ? null
    : last_attempt_status === "cloud" ? "segmentNoValueCloud"
    : last_attempt_status === "outside_footprint" ? "segmentNoValueOutside"
    : last_attempt_status === "unavailable" ? "segmentNoValueUnavailable"
    : "segmentNoValueNever";

  const background = measured
    ? rampColor(ndvi_mean)
    : HATCH[last_attempt_status] ?? HATCH.none;

  const age = fmtAge(last_valid_age_days, t);
  const carried = measured && last_valid_age_days > CARRY_FORWARD_DAYS;
  const partial = measured && typeof valid_fraction === "number" && valid_fraction < PARTIAL_VIEW_BELOW;
  const delta = fmtDelta(ndvi_vs_baseline);

  return (
    <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-start", mt: 0.5 }}>
      <Box sx={{ mt: "2px" }}><Swatch background={background} /></Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11.5, color: "#111827", fontWeight: 600, lineHeight: 1.3 }}>
          {spanLabel}{" · "}
          {measured
            ? <>NDVI {fmtNdvi(ndvi_mean)}</>
            : <Box component="span" sx={{ fontWeight: 500, color: "#6b7280" }}>{t(reasonKey)}</Box>}
        </Typography>

        {/* Age is unconditional on a measured segment. A value with no date
            beside it invites being read as current, which is the whole reason
            the view computes the age at query time rather than storing it. */}
        {measured && age && (
          <Typography sx={{ fontSize: 11, color: carried ? "#b45309" : "#6b7280", lineHeight: 1.3 }}>
            {age}
            {carried ? ` · ${t("segmentCarriedForward")}` : ""}
          </Typography>
        )}

        {partial && (
          <Typography sx={{ fontSize: 11, color: "#b45309", lineHeight: 1.3 }}>
            {t("segmentPartialView", { pct: Math.round(valid_fraction * 100) })}
          </Typography>
        )}

        {/* The signed difference from this segment's own monthly baseline, with
            how much history that baseline rests on. Never a class, a band or a
            colour: #146 forbids risk classes on thresholds nothing has
            calibrated, and n_obs is what says how much to trust the figure. */}
        {measured && (
          <Typography sx={{ fontSize: 11, color: "#6b7280", lineHeight: 1.3 }}>
            {delta
              ? t("segmentVsBaseline", { delta, n: baseline_n_obs ?? 0 })
              : t("segmentNoBaseline")}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function SegmentSummary({ summary, t, isMobile, chromeLeft = 0, nodeLabels, maxHeight = "none", bottomOffset = 0 }) {
  return (
    <Box sx={{
      bgcolor: "rgba(255,255,255,0.96)", borderRadius: 1.5, boxShadow: 3,
      px: 1.25, py: 1,
      // `min(...)` rather than a flat 220: on a 320 px phone the fixed width plus
      // Leaflet's own 10 px control margin runs off the right edge.
      maxWidth: isMobile ? "min(220px, calc(100vw - 24px))" : 268,
      // Bounded height, because the content is not. Two segments with a label,
      // an age, a partial-view note and a baseline delta each, under a
      // three-line caveat, runs to roughly 300 px — taller than the map is on a
      // phone once the search bar, chip row and weather banner have taken their
      // share. A Leaflet control does not clip or scroll on its own; it just
      // grows upward off the top of the map.
      maxHeight,
      display: "flex", flexDirection: "column",
      // Clears the mobile sidebar trigger, which sits at the same corner: a 48 px
      // button at `bottom: 16`, so it occupies the first 64 px. 56 was not
      // enough and the trigger sat on top of the text — 72 leaves a visible gap
      // rather than just missing it.
      mb: `${bottomOffset}px`,
      // And clears the DESKTOP sidebar rail, which was missed. It is absolutely
      // positioned at left: 0, height: 100%, zIndex 1500, so it covers this
      // corner of the map for its full height — 56px collapsed, 320px open. The
      // mobile trigger above was allowed for and this was not, which left the
      // box mostly hidden behind the rail. The width is passed in rather than
      // duplicated here because the rail animates between the two.
      ml: chromeLeft ? `${chromeLeft}px` : 0,
      transition: "margin-left 0.3s",
      fontFamily: "Inter, system-ui, sans-serif",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#111827", lineHeight: 1.3 }}>
          {t("segmentLayerTitle")}
        </Typography>
        {/* This box is a Leaflet control on top of the map, and on a phone it is
            the only place the per-segment layer is explained at all. Never
            `simulated`: the layer is mounted for superusers only. */}
        <HelpTip topic="segmentNdvi" t={t} placement="right" />
      </Box>

      {/* The one line that must never be dropped for space. #135 exists because
          a comment claimed a reading described a tower when it described the
          ground around it, and this layer is the same claim drawn at 512 px.
          `helpNdviClaim` rather than a caveat of its own: the help tip above
          renders this same string, so the two cannot fall out of step (#171). */}
      <Typography sx={{ fontSize: 10.5, color: "#6b7280", lineHeight: 1.35, mt: 0.25, flexShrink: 0 }}>
        {t("helpNdviClaim")}
      </Typography>

      {/* Only the segment list scrolls. The title and the caveat above stay put,
          because a caveat that scrolls out of view has been dropped for space by
          another route — and the comment above says it must never be. `minHeight:
          0` is what lets a flex child shrink below its content and scroll at all;
          without it the box grows and the bound above does nothing.

          MapControl already calls L.DomEvent.disableScrollPropagation on the
          container, so scrolling in here does not zoom the map underneath. */}
      <Box sx={{ overflowY: "auto", minHeight: 0, flexShrink: 1 }}>
        {summary?.status === "ok" && summary.segments.map((s) => (
          <SegmentRow key={s.segment_id ?? s.seq} segment={s} t={t} nodeLabels={nodeLabels} />
        ))}
      </Box>

      {summary?.status === "empty" && (
        <Typography sx={{ fontSize: 11, color: "#b45309", mt: 0.5, lineHeight: 1.35 }}>
          {t("segmentNoneDefined")}
        </Typography>
      )}

      {summary?.status === "unavailable" && (
        <Typography sx={{ fontSize: 11, color: "#b45309", mt: 0.5, lineHeight: 1.35 }}>
          {t("segmentDetailsUnavailable")}
        </Typography>
      )}

      {!summary && (
        <Typography sx={{ fontSize: 11, color: "#9ca3af", mt: 0.5 }}>
          {t("loading")}
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------

export default function SegmentNdviLayer({
  baseUrl,
  summary,
  maxZoom,
  isMobile = false,
  opacity = 0.8,
  chromeLeft = 0,
  nodeLabels,
  maxHeight,
  bottomOffset,
  t = (k) => k,
}) {
  return (
    <>
      <WMSTileLayer
        url={`${baseUrl}${OWS_PATH}`}
        layers={SEGMENT_LAYER}
        format="image/png"
        transparent={true}
        version="1.3.0"
        // No `styles` parameter: the layer's default style is the committed SLD,
        // set by infrastructure/geoserver/publish.mjs. Naming a style here would
        // be a second place to change when the ramp changes.
        //
        // No `time` parameter either. This layer is one row per segment — the
        // current state — and the TIME dimension lives on
        // samst:segment_ndvi_series, for clients that want a date instead.
        maxZoom={maxZoom}
        opacity={opacity}
        // Tiles land in Leaflet's tilePane, which sits under the overlayPane and
        // the markerPane — so the power lines and every sensor pin stay on top
        // without any z-index bookkeeping here. That ordering is deliberate:
        // this is context around the network, not part of it.
        attribution="Corridor NDVI © Copernicus Sentinel-2, SAMST"
      />
      <MapControl position="bottomleft">
        <SegmentSummary summary={summary} t={t} isMobile={isMobile} nodeLabels={nodeLabels}
                        maxHeight={maxHeight} bottomOffset={bottomOffset}
                        chromeLeft={chromeLeft} />
      </MapControl>
    </>
  );
}
